import React, { useState, useEffect } from 'react';
import { Container, Alert, Spinner, Table } from 'react-bootstrap';
import { Link, useSearchParams } from 'react-router-dom';
import {
  searchTickerAcrossAccounts,
  searchTickerInPipeline,
  promoteBasingToVcp,
  deleteBasingTicker,
  updateBasingItem,
  promoteVcpToReadylist,
  demoteVcpToBasing,
  updateVcpItem,
  promoteToReadylist,
  demoteWatchlistToBasing,
  updateWatchlistItem,
  downgradeToWatchlist,
  deleteReadylistTicker,
  updateAccountTickerTheme,
} from '../services/api';
import './Leaders.css';
import './Watchlist.css';
import BasingRow from '../components/BasingRow';
import VcpRow from '../components/VcpRow';
import VcpEditorModal from '../components/VcpEditorModal';
import WatchlistRow from '../components/WatchlistRow';
import ReadylistRow from '../components/ReadylistRow';
import useQuickBuy from '../hooks/useQuickBuy';

const ROW_REMOVE_ANIMATION_DURATION = 260;

const customStyles = `
    .ticker-search-table tbody tr {
        border-bottom: 1px solid var(--border-color) !important;
        transition: all 0.3s ease-in-out !important;
        font-size: 0.95rem !important;
    }
    .ticker-search-table tbody td {
        padding: 4px 6px !important;
        border-bottom: 1px solid var(--border-color) !important;
        vertical-align: middle !important;
        transition: all 0.3s ease-in-out !important;
    }
    .ticker-search-table tbody tr:hover td {
        color: var(--text-primary) !important;
        font-weight: 500 !important;
        transform: scale(1.02) !important;
    }
    [data-theme="light"] .ticker-search-table tbody tr:hover td { color: #1e40af !important; }
    [data-theme="dark"] .ticker-search-table tbody tr:hover td { color: #93c5fd !important; }
    :root {
        --card-bg: #ffffff;
        --border-color: #d1d5db;
        --text-primary: #374151;
    }
    [data-theme="dark"] {
        --card-bg: #1f2937;
        --border-color: #4b5563;
        --text-primary: #f9fafb;
    }
`;

function TickerSearch() {
  const [searchParams] = useSearchParams();
  const ticker = searchParams.get('ticker');
  const [results, setResults] = useState([]);
  const [pipelineMatches, setPipelineMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [actionNotice, setActionNotice] = useState('');
  const [busyStage, setBusyStage] = useState(null);
  const [removingStages, setRemovingStages] = useState([]);
  const [vcpEditorOpen, setVcpEditorOpen] = useState(false);
  const [editingVcpItem, setEditingVcpItem] = useState(null);

  const fetchAll = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const [holdingsRes, pipelineRes] = await Promise.all([
        searchTickerAcrossAccounts(ticker),
        searchTickerInPipeline(ticker),
      ]);
      setResults(holdingsRes.data);
      setPipelineMatches(pipelineRes.data || []);
      setError('');
    } catch (err) {
      setError('Failed to search for ticker. Please try again.');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    if (!ticker) { setLoading(false); return; }
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticker]);

  const { openQuickBuy, quickBuyModals } = useQuickBuy({
    loadAccounts: true,
    onOrderCreated: () => fetchAll({ silent: true }),
    storageKey: 'readylist_quick_buy_default_fraction',
  });

  const updateMatchItem = (stage, fields) => {
    setPipelineMatches(prev => prev.map(m => (
      m.stage === stage ? { ...m, item: { ...m.item, ...fields } } : m
    )));
  };

  // Actions that move/remove a ticker from a stage: run the API call, fade
  // the row out, then refetch so whichever stage the ticker landed in next
  // (if any) shows up automatically -- rather than hand-simulating the
  // pipeline transition here.
  const runTransition = async (stage, action) => {
    setBusyStage(stage);
    setActionError('');
    try {
      await action();
      setRemovingStages(prev => (prev.includes(stage) ? prev : [...prev, stage]));
      setTimeout(() => {
        setRemovingStages(prev => prev.filter(s => s !== stage));
        fetchAll({ silent: true });
      }, ROW_REMOVE_ANIMATION_DURATION);
    } catch (err) {
      setActionError(err.response?.data?.error || 'Action failed.');
    } finally {
      setBusyStage(null);
    }
  };

  const showNotice = (text) => {
    setActionNotice(text);
    setTimeout(() => setActionNotice(''), 4000);
  };

  // --- Basing ---
  const handleBasingPromote = (id) => runTransition('basing', () => promoteBasingToVcp(id));
  const handleBasingRemove = (id) => runTransition('basing', () => deleteBasingTicker(id));
  const handleBasingPriceToggle = async (item, nextValue) => {
    if (item.is_purchased) return;
    updateMatchItem('basing', { include_in_price_updates: nextValue });
    try {
      const response = await updateBasingItem(item.id, { include_in_price_updates: nextValue });
      updateMatchItem('basing', response.data);
    } catch (err) {
      updateMatchItem('basing', { include_in_price_updates: !nextValue });
      setActionError(err.response?.data?.error || 'Failed to update price update setting.');
    }
  };

  // --- VCP ---
  const handleVcpPromote = (id) => runTransition('vcp', () => promoteVcpToReadylist(id));
  const handleVcpDemote = (id) => runTransition('vcp', () => demoteVcpToBasing(id));
  const handleVcpPriceToggle = async (item, nextValue) => {
    if (item.is_purchased) return;
    updateMatchItem('vcp', { include_in_price_updates: nextValue });
    try {
      const response = await updateVcpItem(item.id, { include_in_price_updates: nextValue });
      updateMatchItem('vcp', response.data);
    } catch (err) {
      updateMatchItem('vcp', { include_in_price_updates: !nextValue });
      setActionError(err.response?.data?.error || 'Failed to update price update setting.');
    }
  };
  const openVcpEditor = (item) => {
    setEditingVcpItem(item);
    setVcpEditorOpen(true);
  };
  const closeVcpEditor = () => {
    setVcpEditorOpen(false);
    setEditingVcpItem(null);
  };
  const handleVcpSave = async (itemId, payload) => {
    const response = await updateVcpItem(itemId, payload);
    updateMatchItem('vcp', response.data || payload);
  };

  // --- Watchlist ---
  const handleWatchlistPromote = async (id) => {
    setBusyStage('watchlist');
    setActionError('');
    try {
      await promoteToReadylist(id);
      // Unlike the other stages, promoting from Watchlist doesn't remove the
      // row -- the ticker stays in Watchlist until deleted, same as
      // Watchlist.js's own handlePromote. Just refresh so the new Readylist
      // match appears alongside it.
      showNotice('Promoted to Readylist. It remains in Watchlist until removed.');
      await fetchAll({ silent: true });
    } catch (err) {
      setActionError(err.response?.data?.error || 'Failed to promote ticker.');
    } finally {
      setBusyStage(null);
    }
  };
  const handleWatchlistDemote = (id) => runTransition('watchlist', () => demoteWatchlistToBasing(id));
  const handleWatchlistPriceToggle = async (item, nextValue) => {
    if (item.is_purchased) return;
    updateMatchItem('watchlist', { include_in_price_updates: nextValue });
    try {
      const response = await updateWatchlistItem(item.id, { include_in_price_updates: nextValue });
      updateMatchItem('watchlist', response.data);
    } catch (err) {
      updateMatchItem('watchlist', { include_in_price_updates: !nextValue });
      setActionError(err.response?.data?.error || 'Failed to update price update setting.');
    }
  };
  const handleWatchlistPriorityChange = async (item, newValue) => {
    const previousValue = item.priority_stars || 0;
    if (newValue === previousValue) return;
    updateMatchItem('watchlist', { priority_stars: newValue });
    try {
      await updateWatchlistItem(item.id, { priority_stars: newValue });
    } catch (err) {
      updateMatchItem('watchlist', { priority_stars: previousValue });
      setActionError(err.response?.data?.error || 'Failed to update leader rating.');
    }
  };

  // --- Readylist ---
  const handleReadylistDowngrade = (id) => runTransition('readylist', () => downgradeToWatchlist(id));
  const handleReadylistRemove = (id) => runTransition('readylist', () => deleteReadylistTicker(id));
  const handleReadylistThemeToggle = async (item) => {
    const accountsForTheme = item.theme_accounts || [];
    if (accountsForTheme.length !== 1 || !accountsForTheme[0].account_id) return;
    const targetAccount = accountsForTheme[0];
    const nextValue = !targetAccount.theme_enabled;
    try {
      await updateAccountTickerTheme(targetAccount.account_id, item.ticker_id, nextValue);
      const updatedAccounts = accountsForTheme.map(account => (
        account.account_id === targetAccount.account_id
          ? { ...account, theme_enabled: nextValue }
          : account
      ));
      updateMatchItem('readylist', {
        theme_accounts: updatedAccounts,
        theme_enabled: updatedAccounts.some(account => account.theme_enabled),
      });
    } catch (err) {
      setActionError(err.response?.data?.error || 'Failed to toggle theme.');
    }
  };

  // --- Category (shared handler shape across all four stages) ---
  const handleCategoryUpdate = (stage) => (updatedItem) => {
    updateMatchItem(stage, { category_id: updatedItem.category_id, category: updatedItem.category });
  };

  if (loading) {
    return (
      <Container>
        <div className="text-center my-5">
          <Spinner animation="border" role="status"><span className="visually-hidden">Loading...</span></Spinner>
        </div>
      </Container>
    );
  }

  if (!ticker) {
    return <Container><Alert variant="info" className="my-4">Please enter a ticker symbol to search.</Alert></Container>;
  }

  const hasAnyResult = results.length > 0 || pipelineMatches.length > 0;
  const basingMatch = pipelineMatches.find(m => m.stage === 'basing');
  const vcpMatch = pipelineMatches.find(m => m.stage === 'vcp');
  const watchlistMatch = pipelineMatches.find(m => m.stage === 'watchlist');
  const readylistMatch = pipelineMatches.find(m => m.stage === 'readylist');

  const sectionHeading = (match) => (
    <div className="d-flex justify-content-between align-items-center mb-2">
      <h5 className="mb-0">{match.list}</h5>
      <Link to={match.path} style={{ fontSize: '0.85rem' }}>View full list &rarr;</Link>
    </div>
  );

  return (
    <Container>
      <style>{customStyles}</style>

      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
      {actionError && <Alert variant="danger" className="mb-3" onClose={() => setActionError('')} dismissible>{actionError}</Alert>}
      {actionNotice && <Alert variant="success" className="mb-3" onClose={() => setActionNotice('')} dismissible>{actionNotice}</Alert>}

      {!hasAnyResult && (
        <Alert variant="info">No results found for "{ticker}". Please check the ticker symbol and try again.</Alert>
      )}

      {basingMatch && (
        <div className="mb-4">
          {sectionHeading(basingMatch)}
          <div className="tickers-table-container">
            <div className="table-responsive">
              <table className="ticker-table tickers-table" style={{ tableLayout: 'auto', width: '100%' }}>
                <thead>
                  <tr>
                    <th className="text-center" style={{ width: '40px' }}>#</th>
                    <th style={{ width: '60px' }}>Ticker</th>
                    <th className="text-center" style={{ width: '80px' }}>Notes</th>
                    <th className="text-end" style={{ width: '60px' }}>Price</th>
                    <th className="text-end" style={{ width: '60px' }}>Gain %</th>
                    <th className="text-center" style={{ width: '60px' }}>Status</th>
                    <th className="text-center" style={{ width: '90px' }}>Update</th>
                    <th className="text-end" style={{ width: '172px' }}>Category</th>
                    <th className="text-end" style={{ width: '180px' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  <BasingRow
                    item={basingMatch.item}
                    index={0}
                    selected={false}
                    highlighted={false}
                    removing={removingStages.includes('basing')}
                    deleting={busyStage === 'basing'}
                    globalPriceUpdateLoading={false}
                    onRowClick={() => {}}
                    onPriceUpdateToggle={handleBasingPriceToggle}
                    onCategoryUpdate={handleCategoryUpdate('basing')}
                    onPromote={handleBasingPromote}
                    onRemove={handleBasingRemove}
                  />
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {vcpMatch && (
        <div className="mb-4">
          {sectionHeading(vcpMatch)}
          <div className="tickers-table-container">
            <div className="table-responsive">
              <table className="ticker-table tickers-table" style={{ tableLayout: 'auto', width: '100%' }}>
                <thead>
                  <tr>
                    <th className="text-center" style={{ width: '40px' }}>#</th>
                    <th style={{ width: '60px' }}>Ticker</th>
                    <th className="text-center" style={{ width: '80px' }}>Notes</th>
                    <th className="text-center" style={{ width: '120px' }}>VCP</th>
                    <th className="text-end" style={{ width: '60px' }}>Price</th>
                    <th className="text-end" style={{ width: '60px' }}>Gain %</th>
                    <th className="text-center" style={{ width: '60px' }}>Status</th>
                    <th className="text-center" style={{ width: '90px' }}>Update</th>
                    <th className="text-end" style={{ width: '172px' }}>Category</th>
                    <th className="text-end" style={{ width: '180px' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  <VcpRow
                    item={vcpMatch.item}
                    index={0}
                    selected={false}
                    highlighted={false}
                    removing={removingStages.includes('vcp')}
                    deleting={busyStage === 'vcp'}
                    globalPriceUpdateLoading={false}
                    onRowClick={() => {}}
                    onEditVcp={openVcpEditor}
                    onPriceUpdateToggle={handleVcpPriceToggle}
                    onCategoryUpdate={handleCategoryUpdate('vcp')}
                    onPromote={handleVcpPromote}
                    onDemote={handleVcpDemote}
                  />
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {watchlistMatch && (
        <div className="mb-4">
          {sectionHeading(watchlistMatch)}
          <div className="tickers-table-container">
            <div className="table-responsive">
              <table className="ticker-table tickers-table" style={{ tableLayout: 'auto', width: '100%' }}>
                <thead>
                  <tr>
                    <th className="text-center" style={{ width: '40px' }}>#</th>
                    <th className="text-center" style={{ width: '104px' }}>Leader</th>
                    <th style={{ width: '60px' }}>Ticker</th>
                    <th className="text-center" style={{ width: '80px' }}>Notes</th>
                    <th className="text-end" style={{ width: '60px' }}>Gain %</th>
                    <th className="text-center" style={{ width: '60px' }}>Status</th>
                    <th className="text-center" style={{ width: '90px' }}>Update</th>
                    <th className="text-end" style={{ width: '172px' }}>Category</th>
                    <th className="text-end" style={{ width: '180px' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  <WatchlistRow
                    item={watchlistMatch.item}
                    index={0}
                    selected={false}
                    highlighted={false}
                    removing={removingStages.includes('watchlist')}
                    deleting={busyStage === 'watchlist'}
                    globalPriceUpdateLoading={false}
                    priorityLoading={false}
                    onRowClick={() => {}}
                    onPriorityChange={handleWatchlistPriorityChange}
                    onPriceUpdateToggle={handleWatchlistPriceToggle}
                    onCategoryUpdate={handleCategoryUpdate('watchlist')}
                    onPromote={handleWatchlistPromote}
                    onDemote={handleWatchlistDemote}
                  />
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {readylistMatch && (
        <div className="mb-4">
          {sectionHeading(readylistMatch)}
          <div className="tickers-table-container">
            <div className="table-responsive">
              <table className="ticker-table tickers-table" style={{ tableLayout: 'auto', width: '100%' }}>
                <thead>
                  <tr>
                    <th className="text-center" style={{ width: '40px' }}>#</th>
                    <th style={{ width: '60px' }}>Ticker</th>
                    <th className="text-center" style={{ width: '50px' }}>Trade</th>
                    <th className="text-center" style={{ width: '110px' }}>Notes</th>
                    <th className="text-end" style={{ width: '60px' }}>Price</th>
                    <th className="text-end" style={{ width: '60px' }}>Gain %</th>
                    <th className="text-center" style={{ width: '60px' }}>Status</th>
                    <th className="text-end" style={{ width: '172px' }}>Category</th>
                    <th className="text-center" style={{ width: '80px' }}>Thm</th>
                    <th className="text-end" style={{ width: '130px' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  <ReadylistRow
                    item={readylistMatch.item}
                    index={0}
                    selected={false}
                    highlighted={false}
                    removing={removingStages.includes('readylist')}
                    deleting={busyStage === 'readylist'}
                    themeToggleLoading={false}
                    onRowClick={() => {}}
                    onQuickBuy={openQuickBuy}
                    onCategoryUpdate={handleCategoryUpdate('readylist')}
                    onThemeToggle={handleReadylistThemeToggle}
                    onDowngrade={handleReadylistDowngrade}
                    onRemove={handleReadylistRemove}
                  />
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Holdings table */}
      {results.length > 0 && (
        <>
          <h5 className="mb-2">Holdings <span className="text-muted" style={{ fontSize: '0.85rem' }}>({results.length})</span></h5>
          <Table bordered hover responsive className="ticker-search-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Portfolio</th>
                <th>Account</th>
                <th>Shares</th>
                <th>Cost Basis</th>
                <th>Effective Price</th>
                <th>Current Value</th>
                <th>Total Gain/Loss</th>
              </tr>
            </thead>
            <tbody>
              {results.map((result, index) => (
                <tr key={result.account_id}>
                  <td style={{ width: '50px', color: '#888', fontSize: '0.85rem' }}>{index + 1}</td>
                  <td><Link to={`/portfolio/${result.portfolio_id}`}>{result.portfolio_name}</Link></td>
                  <td><Link to={`/account/${result.account_id}`}>{result.account_name}</Link></td>
                  <td>{result.shares.toFixed(2)}</td>
                  <td>${result.cost_basis.toFixed(2)}</td>
                  <td>
                    ${(result.effective_price || result.current_price).toFixed(2)}
                    {result.manual_price && <span className="text-info ms-1" title="Manual price is set">📝</span>}
                  </td>
                  <td>${result.current_value.toFixed(2)}</td>
                  <td>
                    <strong>${result.total_gain.toFixed(2)}</strong>{' '}
                    (<span className={result.total_gain >= 0 ? 'text-success' : 'text-danger'}>
                      <strong>{result.total_gain_percentage >= 0 ? '+' : ''}{result.total_gain_percentage.toFixed(2)}%</strong>
                    </span>)
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}

      <VcpEditorModal
        show={vcpEditorOpen}
        item={editingVcpItem}
        onClose={closeVcpEditor}
        onSave={handleVcpSave}
      />
      {quickBuyModals}
    </Container>
  );
}

export default TickerSearch;
